<template>
  <div>
    <LoadingState v-if="!status.isLoaded" :size="50" class="pt-2" />
    <div v-if="status.isLoaded && other">
      <PageTitle>
        <div class="mr-2">{{ other.description }}</div>
        <HorizontalList :items="headerItems()" />
        <template #actions>
          <DropDown :items="menuItems()">
            <Btn>{{ $t("global.actions") }}</Btn>
          </DropDown>
        </template>
      </PageTitle>
      <Card :title="$t('global.service')" class="mb-4">
        <CardDataHighlight :title="other.description" :data="other.billing.price" color="tau" />
      </Card>
      <AddonsTable v-if="showAddons()" :addons="other.addons" :status="status" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import { getContactSupport, getDeleteService } from "@/utils/serviceMenuOptions";
import AddonsTable from "@/components/smart/tables/AddonsTable";
import ApacCdnModal from "@/components/smart/modals/ApacCdn";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import CardDataHighlight from "@/components/display/card-sub-components/CardDataHighlight";
import DropDown from "@/components/display/DropDown";
import { getPurgeCache } from "@/utils/serviceMenuOptions";
import HorizontalList from "@/components/display/HorizontalList";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import PageTitle from "@/components/display/PageTitle";
import SharedSslModal from "@/components/smart/modals/SharedSsl";
import store from "@/store";

export default {
  name: "OtherDetailPage",
  components: {
    AddonsTable,
    Btn,
    Card,
    CardDataHighlight,
    DropDown,
    HorizontalList,
    LoadingState,
    PageTitle,
  },
  props: {
    otherId: [Number, String],
  },
  setup(props, { root }) {
    const other = computed(() => store.getters["others/getItem"]);
    const status = computed(() => store.getters["others/getItemStatus"]);

    const headerItems = () => [
      {
        key: root.$t("global.renewal"),
        value:
          other.value.billing && other.value.billing.formattedNextBillDate
            ? other.value.billing.formattedNextBillDate
            : root.$t("global.na"),
      },
    ];

    const openCdnControlPanel = () => {
      store.dispatch("toasts/addToast", {
        variant: "info",
        message: root.$t("toasts.cdn-control-panel-opening-message"),
      });
      store
        .dispatch("others/getCdnControlPanelUrl", props.otherId)
        .then((response) => {
          let newWindow = window.open("about:blank");
          newWindow.document.write("<!DOCTYPE html>\n<body></body>");
          let form = newWindow.document.createElement("form");
          newWindow.document.body.appendChild(form);
          form.setAttribute("method", "POST");
          form.setAttribute("action", response.url);
          form.submit();
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.cdn-control-panel-error-message", {
              domain: other.value.description,
            }),
          });
        });
    };

    const openApacCdn = (enable) => {
      store.dispatch("modals/open", {
        template: ApacCdnModal,
        data: { enable, service: other.value },
      });
    };

    const openSharedSsl = (enable) => {
      store.dispatch("modals/open", {
        template: SharedSslModal,
        data: { enable, service: other.value },
      });
    };

    const menuItems = () => {
      const items = [];

      const edgecast = other.value.edgecast;
      if (edgecast) {
        items.push(
          new NavLink(root.$t("global.cdn-control-panel"), null, openCdnControlPanel),
          getPurgeCache(other.value)
        );
        if (edgecast?.apacEnabled === false) {
          items.push(new NavLink(root.$t("global.enable-apac-cdn"), null, () => openApacCdn(true)));
        }
        if (edgecast?.apacEnabled === true) {
          items.push(
            new NavLink(root.$t("global.disable-apac-cdn"), null, () => openApacCdn(false))
          );
        }
        if (edgecast?.sharedSslEnabled === false) {
          items.push(
            new NavLink(root.$t("global.enable-shared-ssl"), null, () => openSharedSsl(true))
          );
        }
        if (edgecast?.sharedSslEnabled === true) {
          items.push(
            new NavLink(root.$t("global.disable-shared-ssl"), null, () => openSharedSsl(false))
          );
        }
      }

      items.push(getContactSupport(other.value), getDeleteService(other.value));

      return items.filter((i) => i !== null);
    };

    const showAddons = () => other.value?.addons?.length > 0;

    onMounted(() => {
      store.dispatch("others/get", props.otherId);
    });

    return {
      headerItems,
      menuItems,
      other,
      showAddons,
      status,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";

.host-attributes {
  font-size: 0.875rem;
  .attribute {
    display: flex;
    align-items: baseline;
    @include media-breakpoint-up(md) {
      &:not(:last-child) {
        &::after {
          margin-right: 1rem;
          margin-left: 1rem;
          content: "|";
        }
      }
    }
    .field {
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
    .value {
      font-weight: bold;
    }
  }
}
</style>
